import {
  useEffect,
  useRef,
  useState,
} from 'react';

import Dynamicore, { SERVICES } from 'services/dynamicore';
import { getCurrentSessionData } from 'utils/aws/cognito';
import { getSignedUrl, upload } from 'utils/aws/s3';

import { toBase64 } from '../constants';
import useDragAndDrop from './useDragAndDrop';

const MAX_DOCUMENT_RETRIES = 3;
const DOCUMENT_RETRY_WINDOW_MS = 60 * 1000;

//Manejo de carga de archivos, conversion PDF, extraccion de texto y manejo de errores para la edicion del archivo
export default function useFileUpload({
  defaultFile,
  defaultFileName,
  name,
  onChange,
  events,
  accept,
  base64,
  error,
  isMobile,
  getTextFromPDF,
  onBeforeFileChange,
  onDocumentAnalyzed,
  company,
  setSignatures,
  handleSidebarToggle,
}) {
  const [text, setText] = useState('');
  const [currentError, setCurrentError] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [originFile, setOriginFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showMobileAlert, setShowMobileAlert] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [retryBlockedUntil, setRetryBlockedUntil] = useState(0);
  const pendingLoad = useRef(null);
  const uploading = useRef(false);
  const retryTimestamps = useRef([]);

  //Carga de archivo por defecto
  const loadDefaultFile = async function (file = defaultFile, fileName = defaultFileName) {
    if (file !== undefined && !uploading.current) {
      pendingLoad.current = {
        type: 'default',
        file,
        fileName,
        failed: false,
      };
      uploading.current = true;
      setHasLoadError(false);
      setIsUploading(true);
      setCurrentError(false);
      setPdfFile(null);
      setOriginFile(null);

      try {
        const renamedFile = new File([file], fileName, {
          type: file.type,
          lastModified: file.lastModified,
        });

        setText(fileName);

        const target = {
          name,
          value: renamedFile,
        };
        const isPdf = file.type === 'application/pdf'
          || file.name.split('.').pop().toLowerCase() === 'pdf';
        const fileUrl = isPdf
          ? URL.createObjectURL(renamedFile)
          : await transformDocToPdf(file);

        await getTextFromPDF(fileUrl, {
          preserveExisting: true,
        });
        setPdfFile(fileUrl);
        setOriginFile(isPdf ? null : URL.createObjectURL(renamedFile));

        onChange({
          target,
        });

        if (events?.onChange) {
          events.onChange(target);
        }
      } catch (error) {
        console.error('Error al cargar el archivo:', error);
        pendingLoad.current.failed = true;
        setHasLoadError(true);
        setCurrentError(error.message || 'Error al cargar el archivo');
        setPdfFile(null);
        setOriginFile(null);
      } finally {
        uploading.current = false;
        setIsUploading(false);
      }
    }
  };

  const transformDocToPdf = async (file) => {
    try {
      if (!company?.id) {
        throw new Error('No se encontró la empresa para convertir el archivo a PDF');
      }

      const currentSessionData = getCurrentSessionData();
      const { Location: url } = await upload(
        `company/${company?.id}/documents/${file?.name}`,
        file,
        {
          company: company?.id,
          user: currentSessionData?.userData?.UserAttributes?.sub,
        },
        company?.id,
      );
      const response = await Dynamicore(SERVICES.DOC_TO_PDF).post({
        url,
      });

      if (typeof response?.name !== 'string' || !response.name.trim()) {
        throw new Error('El servicio de conversión no devolvió un archivo PDF');
      }

      const signedUrl = await getSignedUrl(response.name, 1000);
      if (typeof signedUrl !== 'string' || !signedUrl.trim()) {
        throw new Error('No se pudo obtener la URL del archivo PDF convertido');
      }

      return signedUrl;
    } catch (error) {
      console.error('Error al convertir a PDF:', error);
      throw error;
    }
  };

  //Cargar archivo
  const handleFileChange = async function ({ target: { files } }, isRetry = false) {
    if (!files?.length || uploading.current) return;

    pendingLoad.current = {
      type: 'selected',
      files: Array.from(files),
      failed: false,
    };
    uploading.current = true;
    setHasLoadError(false);

    setCurrentError(false);
    setIsUploading(true);
    setPdfFile(null);
    setOriginFile(null);
    const names = [];
    let filesOutput = [];
    const validExt = accept.map((_) => _?.toLowerCase());
    setText(Array.from(files, (file) => file.name).join(', '));

    if (!base64) {
      filesOutput = files;
    }

    try {
      if (!isRetry) onBeforeFileChange();
      for await (const file of files) {
        const ext = (file.name.split('.').pop() || '').toLowerCase();

        names.push(file.name);

        if (base64) {
          filesOutput.push(await toBase64(file));
        }

        if (validExt.length && !validExt.includes(ext)) {
          throw new Error('Invalid file type');
        }

        const isPdf = file.type === 'application/pdf' || ext === 'pdf';
        const fileUrl = isPdf
          ? URL.createObjectURL(file)
          : await transformDocToPdf(file);

        await getTextFromPDF(fileUrl);
        setPdfFile(fileUrl);
        setOriginFile(isPdf ? null : URL.createObjectURL(file));
        handleSidebarToggle(0, true);
      }

      setText(names.join(', '));

      const target = {
        name,
        value: filesOutput[0],
      };

      onChange({
        target,
      });

      if (events?.onChange) {
        events.onChange(target);
      }

      setSignatures([]);
      onDocumentAnalyzed();
    } catch (error) {
      console.error('Error al cargar el archivo:', error);
      pendingLoad.current.failed = true;
      setHasLoadError(true);
      setCurrentError(error.message || 'Error al cargar el archivo');
      setPdfFile(null);
      setOriginFile(null);
    } finally {
      uploading.current = false;
      setIsUploading(false);
    }
  };

  const handleRetryLoad = async () => {
    if (uploading.current || !pendingLoad.current?.failed) return;

    const now = Date.now();
    retryTimestamps.current = retryTimestamps.current.filter(
      (timestamp) => now - timestamp < DOCUMENT_RETRY_WINDOW_MS,
    );

    if (retryTimestamps.current.length >= MAX_DOCUMENT_RETRIES) {
      setRetryBlockedUntil(retryTimestamps.current[0] + DOCUMENT_RETRY_WINDOW_MS);
      return;
    }

    retryTimestamps.current.push(now);
    if (retryTimestamps.current.length === MAX_DOCUMENT_RETRIES) {
      setRetryBlockedUntil(retryTimestamps.current[0] + DOCUMENT_RETRY_WINDOW_MS);
    }

    const request = pendingLoad.current;
    if (request.type === 'default') {
      await loadDefaultFile(request.file, request.fileName);
    } else {
      await handleFileChange({
        target: {
          files: request.files,
        },
      }, true);
    }
  };

  //Descargar PDF
  const handleDownloadPdf = () => {
    if (pdfFile) {
      const link = document.createElement('a');
      link.href = originFile || pdfFile;
      link.download = text; // Puedes cambiar el nombre del archivo aquí
      link.click();
    }
  };

  const {
    isDragging,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useDragAndDrop({
    onFiles: handleFileChange,
  });

  //Carga de archivo por defecto
  useEffect(() => {
    loadDefaultFile();
  }, [defaultFile]);

  useEffect(() => {
    if (!retryBlockedUntil) return undefined;

    const timeout = setTimeout(() => {
      setRetryBlockedUntil(0);
    }, Math.max(0, retryBlockedUntil - Date.now()));

    return () => clearTimeout(timeout);
  }, [retryBlockedUntil]);

  useEffect(() => {
    // Para mostrar el Alert en moviles
    if (pdfFile && isMobile) {
      setShowMobileAlert(true);
    }
  }, [pdfFile, isMobile]);

  //Manejo de error
  useEffect(() => {
    setCurrentError(error);
  }, [error]);

  return {
    text,
    currentError,
    pdfFile,
    isUploading,
    hasLoadError,
    isRetryLimited: Boolean(retryBlockedUntil),
    showMobileAlert,
    isDragging,
    setShowMobileAlert,
    handleFileChange,
    handleRetryLoad,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDownloadPdf,
  };
}
